/**
 * Agent Operations. Every agent is shown inside its chain: person → seat →
 * session → agent. The chain is never collapsed, and an agent is never
 * presented as a colleague.
 */
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { FreshnessTag, Panel, StateChip } from '../components/Primitives';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  SkeletonPanel,
} from '../components/States';
import { relativeTime } from '../lib/format';
import { toneFor } from '../lib/tone';
import { Link } from 'react-router-dom';

export function AgentOperations() {
  const projection = useProjection(() => api.agents(), []);
  const live = useLive(['project.updated', 'portfolio.updated'], projection.reload);

  if (projection.loading) return <SkeletonPanel rows={6} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  const groups = [
    { key: 'attention', title: 'Failed, blocked or stale', match: ['FAILED', 'BLOCKED', 'STALE'] },
    { key: 'waiting', title: 'Waiting', match: ['WAITING', 'PAUSED'] },
    { key: 'working', title: 'Working', match: ['WORKING', 'STARTING'] },
    { key: 'done', title: 'Completed and stopped', match: ['COMPLETED', 'STOPPED'] },
  ];

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Execution workers</div>
          <h1 className="topstrip__title">Agent operations</h1>
          <p className="small dim">
            An agent runs inside a session owned by a seat that belongs to a colleague. Agents are
            not team members and are never counted as capacity on their own.
          </p>
        </div>
        <div className="topstrip__meta">
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      <Panel
        title="Workers by state"
        subtitle="Counts are a control input, never a measure of progress."
      >
        <div className="grid grid--4">
          {Object.entries(view.counts)
            .filter(([, count]) => count > 0)
            .map(([status, count]) => (
              <div key={status} className="stat" data-tone={toneFor('agent', status)}>
                <span className="label">{status.toLowerCase()}</span>
                <span className="stat__value" style={{ color: 'var(--tone)' }}>
                  {count}
                </span>
              </div>
            ))}
          {view.agents.length === 0 && (
            <p className="small dim">No agents have been observed for this organisation.</p>
          )}
        </div>
      </Panel>

      {view.agents.length === 0 ? (
        <EmptyState
          title="No agents observed"
          body="No provider has reported an agent for this organisation yet. This is an empty result, not a failure."
        />
      ) : (
        groups.map((group) => {
          const agents = view.agents.filter((agent) => group.match.includes(agent.status));
          if (agents.length === 0) return null;
          return (
            <Panel key={group.key} title={`${group.title} (${agents.length})`} flush>
              <div className="rows">
                <div
                  className="row row--head desktop-only"
                  style={{ gridTemplateColumns: '1.1fr 1fr 1.2fr 1fr 0.9fr' }}
                >
                  <span>Agent</span>
                  <span>State</span>
                  <span>Assignment</span>
                  <span>Owner and session</span>
                  <span>Evidence</span>
                </div>
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="row"
                    style={{ gridTemplateColumns: '1.1fr 1fr 1.2fr 1fr 0.9fr' }}
                    data-tone={toneFor('agent', agent.status)}
                  >
                    <div className="row__stack row__cell" data-label="Agent">
                      <strong>{agent.name}</strong>
                      <span className="row__sub">
                        <Link to={`/projects/${agent.projectId}/control`}>{agent.projectName}</Link>{' '}
                        · {agent.workstream}
                      </span>
                    </div>
                    <div className="row__stack row__cell" data-label="State">
                      <StateChip kind="agent" state={agent.status} />
                      <span className="row__sub">{agent.statusDetail}</span>
                      {agent.rateLimited && (
                        <span className="chip" data-tone="risk">
                          rate limited
                        </span>
                      )}
                    </div>
                    <div className="row__stack row__cell" data-label="Assignment">
                      <span className="truncate">
                        {agent.workItemKey
                          ? `${agent.workItemKey} ${agent.workItemTitle ?? ''}`
                          : 'no work item'}
                      </span>
                      <span className="row__sub mono truncate">{agent.branch ?? 'no branch'}</span>
                      {agent.waitingOn && (
                        <span className="row__sub">waiting on {agent.waitingOn}</span>
                      )}
                    </div>
                    <div className="row__stack row__cell" data-label="Owner and session">
                      <span className="truncate">{agent.ownerName ?? 'unknown owner'}</span>
                      <span className="row__sub mono">
                        {agent.sessionId} · <StateChip kind="session" state={agent.sessionStatus} />
                      </span>
                    </div>
                    <div
                      className="row__stack row__cell"
                      data-label="Evidence"
                      style={{ alignItems: 'flex-end' }}
                    >
                      <span className="row__sub mono">{agent.lastCommitSha ?? 'no commit'}</span>
                      {agent.lastBuildStatus && (
                        <StateChip kind="build" state={agent.lastBuildStatus} />
                      )}
                      <FreshnessTag freshness={agent.freshness} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          );
        })
      )}

      <p className="tiny dim">
        Generated {relativeTime(view.generatedAt)}. An agent that stopped reporting inside its
        freshness window is shown as STALE regardless of the last state it claimed.
      </p>
    </>
  );
}
